import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, Sparkle } from "lucide-react";
import { HeroCanvas } from "./HeroCanvas";
import { MaskedLines } from "./Reveal";
import { MagneticButton } from "./MagneticButton";
import { scrollToId, imgFallback } from "../utils";
import { HEADSHOT_FALLBACK } from "../data/content";

export const Hero = () => (
  <section id="home" data-testid="hero-section" className="spotlight relative flex min-h-screen items-center overflow-hidden">
    <HeroCanvas />
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[5] h-40 bg-gradient-to-t from-[#F8F9FA] to-transparent" />

    <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 px-6 pb-24 pt-32 md:px-10 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.32em] text-[#00BFA5]"
        >
          <Sparkle size={14} /> From the Field to the Future of Pharma Marketing
        </motion.p>

        <h1 className="font-display text-5xl font-black leading-[0.95] tracking-tight text-[#0A192F] sm:text-6xl lg:text-7xl">
          <MaskedLines immediate delay={0.35} lines={["RISHIKESH", "SHARMA"]} />
        </h1>

        <div className="mt-5 font-display text-lg font-bold uppercase tracking-[0.3em] text-[#0A192F]/60 md:text-xl">
          <MaskedLines immediate delay={0.75} lines={["General Manager — Marketing"]} />
        </div>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-[#4A5568] md:text-lg"
        >
          Building Pharma Brands. Creating Market Impact. Shaping the Future.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.25 }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <MagneticButton testId="enter-journey-btn" variant="primary" onClick={() => scrollToId("chapter-1")}>
            Enter My Journey
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </MagneticButton>
          <MagneticButton testId="explore-impact-btn" variant="ghost" onClick={() => scrollToId("impact")}>
            Explore My Impact
          </MagneticButton>
        </motion.div>
      </div>

      <div className="relative lg:col-span-5">
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="relative mx-auto max-w-md"
        >
          <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-[#00E5FF]/25 via-transparent to-[#00BFA5]/25 blur-2xl" />
          <div className="glass relative overflow-hidden rounded-[2rem] p-2">
            <div className="overflow-hidden rounded-[1.6rem]">
              <img
                src="/assets/avatar-hero.png"
                onError={imgFallback(HEADSHOT_FALLBACK)}
                alt="Rishikesh Sharma — 3D executive avatar"
                data-testid="hero-avatar"
                className="animate-float w-full object-cover"
              />
            </div>
            <div className="pointer-events-none absolute inset-x-8 top-0 h-16 overflow-hidden opacity-40">
              <div className="animate-scan h-8 w-full bg-gradient-to-b from-transparent via-[#00E5FF]/50 to-transparent" />
            </div>
          </div>
          <div className="glass absolute -bottom-5 -left-6 rounded-2xl px-5 py-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#0A192F]/50">Pharma Marketing</p>
            <p className="font-display text-sm font-black text-[#0A192F]">Command Centre</p>
          </div>
        </motion.div>
      </div>
    </div>

    <motion.button
      type="button"
      onClick={() => scrollToId("chapter-1")}
      data-testid="scroll-down-btn"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.8, duration: 0.8 }}
      className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 bg-transparent text-[#0A192F]/60 transition-colors hover:text-[#0A192F]"
      aria-label="Scroll to the journey"
    >
      <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
        <ChevronDown size={26} />
      </motion.div>
    </motion.button>
  </section>
);
